import { HttpClient, HttpHeaders } from "@angular/common/http"
import { Injectable, signal } from "@angular/core"
import { Router } from "@angular/router"
import { LangService } from "@app/services/lang.service"
import { ModalService } from "@app/services/modal.service"
import { environment } from "@env/environment"

const contentType = { "Content-Type": "application/json" } //normal content type for api calls
const contentTypeFileUpload = { enctype: "multipart/form-data" } //content type specifically for uploading files
const httpOptions = {
	//regular http options
	withCredentials: true,
	headers: new HttpHeaders(contentType),
}
const httpOptionsFileUpload = {
	//upload specific http options
	withCredentials: true,
	headers: new HttpHeaders(contentTypeFileUpload),
}
const httpOptionsFileDownload: any = { ...httpOptions, responseType: "blob" } //download file specific http options
const httpOptionsFileDownloadObserve: any = {
	...httpOptionsFileDownload,
	observe: "response",
}
// const urlBase = "http://manage-5.my/api/v2";
const urlBase = "/v1" //the api url to send to (changes by local/dev/production)

@Injectable({
	providedIn: "root",
})
export class ApiService {
	postLogin: string = "" //if arrived from a different url and redirected to login -  holds the url to navigate to after login
	csrf = "" //holds the crsf code (from the cookie)
	curCsrfErrors: number = 0 //counter for csrf errors (currently all api errors). will redirect to network error if too many
	isSendingApiPerms: boolean = false //flag for "currently sending api permissions". to prevent and queue sending gen_items requests before
	permWaitingFuncs: any[] = [] //a queue for all gen_items requests waiting for permissions to return
	isUserLoggedIn: boolean = false //logged in (for app html and timeout function)
	timeout: any = null //holds the timeout function to logout
	logoutInMins: number = 15 //default mins to logout (will be overriden by user data)
	lastActivity: Date | null = null //will hold the date object of the most recent activity (mouse move, click..)

	isStarted: boolean = false

	apiCallPerms: any = null

	constructor(
		private http: HttpClient,
		private router: Router,
		public modalService: ModalService,
		public lang: LangService
	) {}

	async verifyCsrf() {
		//on init or api error - request new csrf (from cookie or api if no cookie). CURRENTLY always asks from api
		await this.get("csrf") //request from api then try to attach from cookie
		this.isStarted = true
	}

	createFormUpload(obj: any, filesObj: any = {}) {
		//use an object with keys and values and a files object and merge them into a FormData object (to be able to upload via api)
		const formData: FormData = new FormData()
		// formData.append(fileFieldName, file, file.name);
		Object.keys(obj).forEach((key) => {
			//iterate the keys and add the key-value to the FormData
			formData.append(key, obj[key])
		})
		Object.keys(filesObj).forEach((key) => {
			//add all the files to the FormData
			formData.append(key, filesObj[key])
		})
		return formData
	}

	promise(apiFunc: any): Promise<any> {
		return new Promise((resolve) => {
			apiFunc.subscribe((res: any) => {
				resolve(res)
			})
		})
	}

	postPromise(path: string, obj: any = {}, opts: any = null): Promise<any> {
		return this.promise(
			this.http.post(`${urlBase}/${path}`, obj, opts || httpOptions)
		)
	}
	getPromise(path: string): Promise<any> {
		return this.promise(this.http.get(`${urlBase}/${path}`, httpOptions))
	}

	get(path: string): Promise<any> {
		return this.getPromise(path)
	}
	postWithVars(
		path: string,
		obj: any = {},
		fieldsLimiter: string = ""
	): Promise<any> {
		if (fieldsLimiter) {
			obj = pluck(obj, fieldsLimiter)
		}
		const params = Object.keys(obj)
			.map(
				(key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key])
			)
			.join("&")
		return this.promise(
			this.http.post(`${urlBase}/${path}?${params}`, {}, httpOptions)
		)
	}

	post(path: string, obj: any = {}, fieldsLimiter: string = ""): Promise<any> {
		if (fieldsLimiter) {
			obj = pluck(obj, fieldsLimiter)
		}
		return this.postPromise(path, obj)
	}
	download(
		path: string,
		obj: any = {},
		fieldsLimiter: string = ""
	): Promise<any> {
		if (fieldsLimiter) {
			obj = pluck(obj, fieldsLimiter)
		}
		return this.postPromise(path, obj, httpOptionsFileDownload)
	}
	downloadWithHeaders(path: string, obj: any = {}) {
		return this.postPromise(path, obj, httpOptionsFileDownloadObserve)
	}

	upload(path: string, obj: any): Promise<any> {
		const formData: FormData = new FormData()
		Object.keys(obj).forEach((key) => {
			//iterate the keys and add the key-value to the FormData
			formData.append(key, obj[key])
		})
		return this.postPromise(path, formData, httpOptionsFileUpload)
	}

	postSubscribe(path: string, obj: any, func: any) {
		return this.http
			.post(`${urlBase}/${path}`, obj, httpOptions)
			.subscribe(func)
	}

	decryptInCollection(objectCollection: any[]) {
		//iterate a collection and decrypt all items in it
		objectCollection.forEach((it) => {
			this.decryptItem(it)
		})
	}
	decryptItem(it: any) {
		//iterate all fields of an object, override when _dec exists
		Object.keys(it).forEach((field) => {
			if (field.endsWith("_dec")) {
				//if field ends with "_dec"
				let fieldNoDec = field.substring(0, field.length - 4) //find the base name
				it[fieldNoDec] = it[field] //override the base with the "_dec" value
			}
		})
		it.errors = {} //make sure to add an errors field
	}

	downloadFile(resultStream: any, name: string) {
		//download file from stream (creates a link to it and dl)
		const blob = new Blob([resultStream], { type: "octet/stream" }) //create blob from the stream returned from server
		const url = window.URL.createObjectURL(blob) //create url data
		let el = document.createElement("A") as any //create link element
		el.style.display = "none" //link has no display
		el.href = url //add the url data
		el.download = name //give the download its name
		document.body.appendChild(el) //add link element to document
		el.click() //click the link to force download
		window.URL.revokeObjectURL(url) //free the resource
		document.body.removeChild(el) //remove the link
	}
	openDelayedUrl() {
		const wnd: any = window.open("", "_blank")
		wnd.document.writeln(this.lang.getVal("preview_process"))
		return (url: string) => (wnd.location.href = url)
	}

	previewDownloadImmediate(isImg: boolean = false) {
		const wnd: any = window.open("", "_blank")
		wnd.document.writeln(this.lang.getVal("preview_process"))

		return (resultStream: any) => {
			const type = isImg ? "image/png" : "application/pdf"
			const blob = new Blob([resultStream], { type }) //create blob from the stream returned from server
			const url = wnd.URL.createObjectURL(blob)
			wnd.location.href = url
			// wnd.URL.revokeObjectURL(url)
		}
		//use: const cb = this.apiService.previewDownloadImmediate() ... cb(res)
	}

	previewDownload(resultStream: any) {
		const blob = new Blob([resultStream], { type: "application/pdf" }) //create blob from the stream returned from server
		const url = window.URL.createObjectURL(blob) //create url data
		window.open(url)
		// window.URL.revokeObjectURL(url) //free the resource
	}
	async sendApiGetPerms(cbFunc: any = null) {
		//if permissions were not called - queue the request and request permissions
		if (this.apiCallPerms) {
			return cbFunc ? cbFunc() : null
		} //permissions already loaded - just run the callback function
		if (cbFunc) {
			//if callback function exists - add it to queue
			this.permWaitingFuncs.push(cbFunc)
		}

		if (this.isSendingApiPerms) {
			return
		} //if flagged as already "currently sending api permissions" - return

		this.isSendingApiPerms = true //flag as "currently sending api permissions"

		const res: any = await this.post("get_api_calls_permissions")
		this.isSendingApiPerms = false //flag as false "currently sending api permissions"
		this.apiCallPerms = res //save to table service field
		this.permWaitingFuncs.forEach((cb) => cb()) //run all callback functions
	}
	logoutCommon() {
		this.isStarted = false
		clearTimeout(this.timeout) //clear the timeout
		this.isUserLoggedIn = false
		sessionStorage.removeItem("cliniqData") //remove storage items
		sessionStorage.removeItem("siteData")
	}

	async doLogout() {
		//run logout process (clear timeout, remove storage items, request logout from server, redirect to login page)
		this.logoutCommon()
		await this.post("logout")
		window.location.assign("/login") //redirect to login page
		// this.modalService.closeAllModals();
		// window.scroll(0,0);
	}

	resetLastActivity() {
		this.lastActivity = new Date() //reset last activity date
		localStorage.setItem("lastActivity", this.lastActivity.toISOString())
	}

	async restartLogoutTimeout(overrideMins: number | null = null) {
		//reset the timeout, recreate logout function

		if (!this.isUserLoggedIn) {
			return
		} //not logged in - exit
		if (overrideMins) {
			//if passed minutes to override, override the local field
			this.logoutInMins = overrideMins
		}
		if (!this.lastActivity) {
			let localStorageLastActivity: any = localStorage.getItem("lastActivity")

			if (!localStorageLastActivity) {
				return this.doLogout()
			}
			localStorageLastActivity = localStorageLastActivity.trim()
			try {
				localStorageLastActivity = new Date(localStorageLastActivity)
				if (
					new Date().getTime() - localStorageLastActivity.getTime() >
					this.logoutInMins * 60 * 1000
				) {
					//if no last activity date or is smaller than the logout minutes - logout
					return this.doLogout()
				}
			} catch (err) {
				return this.doLogout()
			}
		}
		if (
			this.lastActivity != null &&
			new Date().getTime() - this.lastActivity.getTime() >
				this.logoutInMins * 60 * 1000
		) {
			//if  last activity date and is smaller than the logout minutes - logout
			return this.doLogout()
		}

		this.resetLastActivity()
		clearTimeout(this.timeout) //clear the timeout
		let timeoutMillis = this.logoutInMins * 60 * 1000 //the timeout in millis

		this.timeout = setTimeout(() => {
			//set a new logout timeout and save to field
			console.log("out....")
			this.doLogout()
		}, timeoutMillis)
	}
	ccReq() {
		return this.http.post(`${urlBase}/cc`, {}, httpOptions)
	}

	delete_gen_item(tableName: string, id: number) {
		return this.post("delete_gen_item", { tableName, id })
	}
	async save_gen_item_files(obj: any) {
		//tries to decrypt the returned item
		const res: any = await this.upload("save_gen_item", obj)
		this.decryptItem(res)
		return { ...res }
	}
	async save_gen_item(tableName: string, item: any) {
		//tries to decrypt the returned item
		const res: any = await this.post("save_gen_item", {
			tableName,
			mode: "add",
			...item,
		})
		this.decryptItem(res)
		return { ...res }
	}

	save_gen_field_file(
		tableName: string,
		id: number,
		fieldName: string,
		newValue: any
	) {
		return this.upload("save_gen_field", { tableName, id, fieldName, newValue })
	}
	save_gen_field(tableName: string, id: any, fieldName: string, newValue: any) {
		return this.post("save_gen_field", { tableName, id, fieldName, newValue })
	}

	async get_gen_items(tableName: string, sendObj: any = null) {
		//enables paging, tries to decrypt the returned collection
		//console.log(tableName)
		const res: any = await this.post("get_gen_items", { tableName, ...sendObj })
		let col = res[tableName]
		//console.log(res[tableName])
		if (col.data) {
			//pagi
			this.decryptInCollection(col.data)
			return { ...res, [tableName]: { ...col } }
		} else {
			this.decryptInCollection(col)
			return { ...res, col }
		}
	}
	async get_gen_items_col(tableName: string, sendObj: any = null) {
		return (await this.get_gen_items(tableName, sendObj))?.[tableName] || []
	}
}

export function pluck(obj: any, fields: string) {
	return fields.split(",").reduce((acc, it) => {
		acc[it] = obj[it]
		return acc
	}, {})
}

export function senderFactory(initObj: { fn: Function; onFinish?: Function }) {
	const isLoading = signal(false)
	return {
		run: (params: any = null, context: any = null) => {
			isLoading.set(true)
			initObj.fn(params).then((result: any) => {
				isLoading.set(false)
				initObj.onFinish?.({ result, params, context })
			})
		},
		isLoading,
	}
}
